import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ClipboardList } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  criteria: { label: "Criteria", className: "bg-blue-600/20 text-blue-400 border-blue-500/30" },
  schedule: { label: "Schedule", className: "bg-green-600/20 text-green-400 border-green-500/30" },
  add_zips: { label: "Add Zips", className: "bg-[#f4cc1e]/20 text-[#f4cc1e] border-[#f4cc1e]/30" },
  remove_zips: { label: "Remove Zips", className: "bg-orange-600/20 text-orange-400 border-orange-500/30" },
};

const ChangeRequestHistory = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await axios.get(`${API}/customer/change-requests`);
        setRequests(response.data || []);
      } catch (error) {
        console.error("Failed to fetch change requests:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, []);

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const d = new Date(isoString);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ClipboardList size={24} className="text-[#f4cc1e]" />
        <h1 className="text-2xl font-bold text-white font-['Chivo']">Change Requests</h1>
        <span className="text-sm text-white/50">{requests.length} total</span>
      </div>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <div className="p-12 text-center text-white/50">
              <ClipboardList size={48} className="mx-auto mb-4 opacity-50" />
              <p>No change requests yet</p>
              <p className="text-sm mt-1">Your submitted requests will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#1a3a6a]">
                    <TableHead className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold">Date</TableHead>
                    <TableHead className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold">Type</TableHead>
                    <TableHead className="bg-[#091c3c] text-[#f4cc1e] uppercase text-[10px] sm:text-xs font-bold">Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((req) => {
                    const typeConfig = TYPE_CONFIG[req.request_type] || { label: req.request_type, className: "" };
                    return (
                      <TableRow key={req.id} onClick={() => setSelected(req)} title="Click to view the full request"
                        className="border-b border-[#1a3a6a] hover:bg-[#0d2a5a] cursor-pointer">
                        <TableCell className="font-mono text-sm text-white whitespace-nowrap">
                          {formatDate(req.created_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={typeConfig.className}>
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-white text-sm max-w-md">
                          <span className="line-clamp-2">{req.message}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Change Request</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={(TYPE_CONFIG[selected.request_type] || {}).className || ""}>
                  {(TYPE_CONFIG[selected.request_type] || {}).label || selected.request_type}
                </Badge>
                {selected.status && <span className="text-xs text-white/60 capitalize">{selected.status}</span>}
                <span className="text-xs text-white/50">· {formatDate(selected.created_at)}</span>
              </div>
              <div className="rounded-lg bg-[#0d2a5a] border border-[#1a3a6a] p-3 max-h-[55vh] overflow-y-auto">
                <p className="text-sm text-white whitespace-pre-wrap break-words">{selected.message || "(no message)"}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChangeRequestHistory;
